/**
 * SettingsPlaceholder — shared "Coming soon" body used by every settings page
 * that hasn't been built out in this pass. Each real page (Profile, Users,
 * Projects, Usage, Storage) replaces this with actual content.
 *
 * Every placeholder names WHY it's not built yet so the sidebar isn't a graveyard
 * of dead links. Phase 2 (billing + notification prefs + role tiers) will
 * replace most of these.
 */

import { Sparkles } from "lucide-react";

type Props = {
  title: string;
  /** One-line explanation of what this page will do when built. */
  description: string;
  /** Which release this ships in — surfaced in the "Not available yet" pill. */
  eta?: "Phase 2" | "Later";
};

export function SettingsPlaceholder({ title, description, eta = "Phase 2" }: Props) {
  return (
    <div>
      <SettingsPageHeader title={title} />
      <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-10 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-muted-foreground">
          <Sparkles className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <p className="mt-4 text-sm text-foreground">{description}</p>
        <span className="mt-4 inline-block rounded-full border border-muted-foreground/30 bg-muted-foreground/10 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {eta === "Phase 2" ? "Coming in Phase 2" : "Coming later"}
        </span>
      </div>
    </div>
  );
}

/**
 * Reusable section header for settings pages. Matches Frame.io's
 * "Suvajit's Account · Profile" pattern by rendering the section name
 * and (optional) subtitle at the top of the content column.
 */
export function SettingsPageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}
